import { IconChevronsRight, IconSparkles } from "@tabler/icons-react"
import { type FC, Fragment, useEffect, useRef, useState } from "react"

import { aiGenerationCreditCosts } from "@forento/core/models/ai"
import { toNumberFormat } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import Button from "~/components/Button"
import Link from "~/components/Link"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import EditProgress from "./EditProgress"
import ProposedChanges from "./ProposedChanges"

const quickPrompts = [
	{ label: "Tighten the intro", message: "Tighten the opening so it gets to the point faster" },
	{ label: "Easier to skim", message: "Make the article easier to skim with clearer sections" },
	{ label: "Improve the SEO title", message: "Improve the title, meta description and excerpt for search" },
]

type Props = {
	isExpanded: boolean
	postId: number
	hasUnsavedChanges: boolean
	onExpandedChange(isExpanded: boolean): void
	onApplied(): Promise<void>
}
const AiBlogPostEditChat: FC<Props> = ({ isExpanded, postId, hasUnsavedChanges, onExpandedChange, onApplied }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const utilities = query.useUtils()

	const [message, setMessage] = useState("")
	const messagesRef = useRef<HTMLDivElement>(null)

	// Edits normally complete within the mutation; a pending turn only appears here when an earlier
	// request was interrupted, so the poll exists to pick up how that turn ended.
	const edits = query.ai.listBlogPostEdits.useQuery(postId, {
		refetchInterval: q =>
			q.state.data?.some(turn => turn.status === "queued" || turn.status === "generating") ? 2000 : false,
	})

	const pendingTurnId = edits.data?.find(turn => turn.status === "queued" || turn.status === "generating")?.id ?? null
	const undecidedTurn =
		edits.data?.find(
			turn =>
				turn.status === "completed" &&
				turn.changes !== null &&
				turn.changes.length > 0 &&
				turn.decision === null,
		) ?? null

	const sendEdit = query.ai.editBlogPost.useMutation({
		async onError(error, variables) {
			console.error(error)
			// Sending clears the input, so a failed message goes back into it to retry or edit.
			setMessage(variables.message)
			await Promise.all([edits.refetch(), user.reload()])
			await alert.show(
				"Failed to send edit request",
				error.message || "An error occurred while sending your edit request. Please try again.",
			)
		},
		async onSuccess() {
			await Promise.all([edits.refetch(), user.reload()])
		},
	})

	const applyEdit = query.ai.applyBlogPostEdit.useMutation({
		async onError(error) {
			console.error(error)
			await edits.refetch()
			await alert.show(
				"Failed to apply changes",
				error.message || "An error occurred while applying the changes. Please try again.",
			)
		},
		async onSuccess(data) {
			await Promise.all([onApplied(), edits.refetch()])
			if (data.skippedOperationCount > 0) {
				await alert.show(
					"Changes were skipped",
					"This post changed after these edits were proposed, so they no longer applied.",
				)
			}
		},
	})

	const isGenerating = sendEdit.isPending || pendingTurnId !== null
	const editProgress = query.ai.getBlogPostEditProgress.useQuery(postId, {
		enabled: isExpanded && isGenerating,
		refetchInterval: isExpanded && isGenerating ? 1000 : false,
	})

	const discardEdit = query.ai.discardBlogPostEdit.useMutation({
		async onError(error) {
			console.error(error)
			await edits.refetch()
			await alert.show(
				"Failed to discard changes",
				error.message || "An error occurred while discarding the changes. Please try again.",
			)
		},
		async onSuccess() {
			await edits.refetch()
		},
	})

	useEffect(() => {
		const element = messagesRef.current
		if (isExpanded && element !== null) element.scrollTop = element.scrollHeight
	}, [isExpanded, edits.data, sendEdit.isPending])

	const editCost = aiGenerationCreditCosts["blog-post-edit"]
	const hasEnoughCredits = platform.aiCreditBalance >= editCost
	const canSend =
		hasEnoughCredits &&
		!hasUnsavedChanges &&
		pendingTurnId === null &&
		undecidedTurn === null &&
		!sendEdit.isPending

	const handleSend = () => {
		const trimmedMessage = message.trim()
		if (!canSend || trimmedMessage.length === 0) return
		utilities.ai.getBlogPostEditProgress.setData(postId, null)
		setMessage("")
		sendEdit.mutate({ postId, message: trimmedMessage })
	}

	// Applying reloads the editor from the saved post, so edits made while the proposal sat here are lost.
	const handleApply = async (requestId: number) => {
		if (hasUnsavedChanges) {
			const dialog = await alert.confirm(
				"Discard your unsaved changes",
				"You have edited this post since these changes were proposed. Applying them replaces the post with the saved version plus the proposed changes, and your unsaved edits are lost.",
				{ confirmLabel: "Discard changes" },
			)
			if (!dialog.result) return

			try {
				await applyEdit.mutateAsync(requestId)
			} catch (error) {
				console.error(error)
				dialog.close()
				return Promise.reject(error)
			}
			dialog.close()
			return
		}

		applyEdit.mutate(requestId)
	}

	const handleDiscard = async (requestId: number) => {
		const dialog = await alert.confirm(
			"Discard changes",
			"Are you sure you want to discard the proposed changes? They cannot be recovered.",
		)
		if (!dialog.result) return

		try {
			await discardEdit.mutateAsync(requestId)
		} catch (error) {
			console.error(error)
			dialog.close()
			return Promise.reject(error)
		}
		dialog.close()
	}

	if (!isExpanded) {
		const undecidedChangeCount = undecidedTurn?.changes?.length ?? null

		return (
			<aside className="border-component-border sticky top-0 flex h-screen w-14 shrink-0 flex-col items-center self-start border-l bg-white py-3">
				<Tooltip tooltip="Edit with Forento AI">
					<button
						type="button"
						onClick={() => onExpandedChange(true)}
						aria-label="Expand Forento AI"
						className="border-primary bg-primary/10 text-primary hover:bg-primary/20 relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 px-2 py-3 transition-colors"
					>
						<IconSparkles className="size-5" />
						<span className="text-xs font-semibold [writing-mode:vertical-rl]">Forento AI</span>
						{undecidedChangeCount !== null && (
							<span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-semibold text-white">
								{undecidedChangeCount > 9 ? "9+" : undecidedChangeCount}
							</span>
						)}
					</button>
				</Tooltip>
			</aside>
		)
	}

	return (
		<aside className="border-component-border sticky top-0 flex h-screen w-100 shrink-0 flex-col self-start border-l bg-white">
			<div className="border-component-border flex items-center justify-between gap-2 border-b px-4 py-3">
				<div className="flex items-center gap-2">
					<IconSparkles className="text-primary size-5" />
					<h2 className="font-semibold">Edit with Forento AI</h2>
				</div>
				<Tooltip tooltip="Collapse">
					<button
						type="button"
						onClick={() => onExpandedChange(false)}
						aria-label="Collapse Forento AI"
						className="cursor-pointer rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
					>
						<IconChevronsRight className="size-5" />
					</button>
				</Tooltip>
			</div>
			<div ref={messagesRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 p-4">
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm text-amber-900">
						<span className="font-semibold">Forento AI is in beta.</span> Nothing is changed until you apply
						the proposed edits, but review them before you do.
					</p>
				</div>
				<div className="max-w-[90%] self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900">
					Tell me what you'd like to change in this post. I can rewrite the article, rework the title and
					search listing, and adjust the tags. You review every change before it is applied.
				</div>
				{edits.data?.map(turn => (
					<Fragment key={turn.id}>
						<div className="bg-primary max-w-[90%] self-end rounded-lg px-3 py-2 text-sm whitespace-pre-line text-white">
							{turn.message}
						</div>
						{turn.status === "completed" ? (
							<>
								<div className="max-w-[90%] self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm whitespace-pre-line text-gray-900">
									{turn.reply ?? "Done."}
								</div>
								{turn.changes !== null && turn.changes.length > 0 && (
									<ProposedChanges
										changes={turn.changes}
										decision={turn.decision}
										isApplying={applyEdit.isPending && applyEdit.variables === turn.id}
										isDiscarding={discardEdit.isPending && discardEdit.variables === turn.id}
										onApply={() => handleApply(turn.id)}
										onDiscard={() => handleDiscard(turn.id)}
									/>
								)}
								<AiGenerationFeedback id={turn.id} type="blog-post-edit" />
							</>
						) : turn.status === "error" ? (
							<div className="max-w-[90%] self-start rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
								Something went wrong while preparing this edit. Please try again.
							</div>
						) : (
							<EditProgress progress={editProgress.data ?? null} />
						)}
					</Fragment>
				))}
				{sendEdit.isPending && (
					<>
						<div className="bg-primary max-w-[90%] self-end rounded-lg px-3 py-2 text-sm whitespace-pre-line text-white">
							{sendEdit.variables?.message}
						</div>
						<EditProgress progress={editProgress.data ?? null} />
					</>
				)}
			</div>
			<div className="border-component-border flex flex-col gap-3 border-t p-4">
				{message.trim().length === 0 && (
					<div className="flex flex-wrap gap-2">
						{quickPrompts.map(prompt => (
							<button
								key={prompt.label}
								type="button"
								onClick={() => setMessage(prompt.message)}
								className="hover:border-primary hover:text-primary cursor-pointer rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700"
							>
								{prompt.label}
							</button>
						))}
					</div>
				)}
				{!hasEnoughCredits && (
					<p className="text-sm text-red-600">
						You need {toNumberFormat(editCost)} AI credits to request an edit. Purchase more credits in
						Billing.
					</p>
				)}
				{/* The edit reads the saved post and applying replaces it, so unsaved work would be lost either way. */}
				{hasUnsavedChanges && (
					<p className="text-sm text-amber-700">
						Save your changes before asking Forento AI to edit this post.
					</p>
				)}
				{undecidedTurn !== null && (
					<p className="text-sm text-amber-700">
						Apply or discard the proposed changes above before sending another message.
					</p>
				)}
				<div className="flex items-end gap-2">
					<textarea
						value={message}
						onChange={event => setMessage(event.target.value)}
						onKeyDown={event => {
							if (event.key === "Enter" && !event.shiftKey) {
								event.preventDefault()
								handleSend()
							}
						}}
						placeholder='Describe a change, like "add a section about pricing"'
						rows={1}
						maxLength={2000}
						className="focus:border-primary field-sizing-content max-h-40 min-h-12 flex-1 resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm focus:outline-none"
					/>
					<Button
						variant="primary"
						isDisabled={!canSend || message.trim().length === 0}
						isLoading={sendEdit.isPending}
						onClick={handleSend}
					>
						Send
					</Button>
				</div>
				<div className="flex items-center justify-between gap-2 text-xs">
					<span className="text-gray-500">
						{toNumberFormat(editCost)} credits per edit · Balance:{" "}
						{toNumberFormat(platform.aiCreditBalance)}
					</span>
					<Link to={routes.settings.billing.plans()} className="text-primary font-medium hover:underline">
						Purchase credits
					</Link>
				</div>
			</div>
		</aside>
	)
}

export default AiBlogPostEditChat
